import { useCallback, useState } from 'react';
import intl from 'react-intl-universal';
import {
  Icon,
  OverflowMenu,
  OverflowMenuItem,
  Typography,
  tailwindOverride,
} from '@getsynapse/design-system';
import { useSelector } from 'react-redux';
import { V2_PATHS, SLICE_STATUS } from 'utils/constants';
import { RootState, useAppThunkDispatch } from 'state/store';
import {
  agentDashboardSelectors,
  hideAgentDashboard,
} from 'state/normalized/agentDashboard/agentDashboardSlice';
import { showNotification as showSnackbarNotification } from 'state/SnackbarNotification/SnackbarNotificationSlice';
import HideDashboardModal from 'Organisms/HideDashboardModal/HideDashboardModal';

const menuItemIconClassName = tailwindOverride(
  'fill-current text-primary',
  'group-hover:fill-current group-hover:text-secondary-darker',
  'group-active:text-neutral-white group-active:fill-neutral-white',
  'text-lg'
);

const menuItemLabelClassName = tailwindOverride(
  'group-active:text-neutral-white',
  'cursor-pointer'
);

/**
 * Overflow (kebab) menu for agent-generated dashboards. Exposes:
 * - "Edit": redirects to the reporting-agent page (separate V2 bundle) with the
 *   dashboard's conversation id, so a full-page navigation is used. Only shown
 *   when the dashboard has a conversation id.
 * - "Hide": opens a confirmation modal that, on confirm, hides the dashboard
 *   from the list. Always available for owned agent dashboards.
 */
export const AgentDashboardOverflowMenu = ({
  dashboardId,
  dashboardName,
  conversationId,
}: {
  dashboardId: string;
  dashboardName: string;
  conversationId?: string;
}) => {
  const dispatch = useAppThunkDispatch();
  const [hideModalOpen, setHideModalOpen] = useState(false);
  const hideStatus = useSelector((state: RootState) =>
    agentDashboardSelectors.selectHideStatus(state)
  );

  const goToConversation = () => {
    window.location.assign(
      `${V2_PATHS.REPORTING_AGENT_PAGE}?conversation=${conversationId}`
    );
  };

  const handleHide = useCallback(async () => {
    try {
      await dispatch(hideAgentDashboard(dashboardId)).unwrap();
      dispatch(
        showSnackbarNotification({
          notificationVariant: 'success',
          notificationTitle: intl.get(
            'HIDE_DASHBOARD_MODAL.SUCCESS_SNACKBAR_MESSAGE',
            { name: dashboardName }
          ),
        })
      );
    } catch (error) {
      dispatch(
        showSnackbarNotification({
          notificationVariant: 'error',
          notificationTitle: intl.get(
            'HIDE_DASHBOARD_MODAL.ERROR_SNACKBAR_MESSAGE'
          ),
        })
      );
    }
  }, [dashboardId, dashboardName, dispatch]);

  return (
    <>
      <HideDashboardModal
        handleHide={handleHide}
        onCloseModal={() => setHideModalOpen(false)}
        shouldDisplayModal={hideModalOpen}
        dashboardName={dashboardName}
        isLoading={hideStatus === SLICE_STATUS.LOADING}
      />
      <OverflowMenu
        menuListProps={{
          'data-cy': `dashboard-${dashboardId}__agent-actions-popover`,
        }}
        menuButtonProps={{
          'data-cy': `dashboard-${dashboardId}__agent-actions-popover-trigger`,
        }}
      >
        {conversationId ? (
          <OverflowMenuItem
            className='group flex items-center space-x-2'
            onSelect={goToConversation}
            data-cy={`dashboard-${dashboardId}__agent-actions-popover__edit`}
          >
            <Icon name='pencil-outline' className={menuItemIconClassName} />
            <Typography variant='label' className={menuItemLabelClassName}>
              {intl.get('DASHBOARDS_LIST_PAGE.TABLE.ACTION.EDIT_CONVERSATION')}
            </Typography>
          </OverflowMenuItem>
        ) : null}
        <OverflowMenuItem
          className='group flex items-center space-x-2'
          onSelect={() => setHideModalOpen(true)}
          data-cy={`dashboard-${dashboardId}__agent-actions-popover__hide`}
        >
          <Icon name='eye-off-outline' className={menuItemIconClassName} />
          <Typography variant='label' className={menuItemLabelClassName}>
            {intl.get('DASHBOARDS_LIST_PAGE.TABLE.ACTION.HIDE')}
          </Typography>
        </OverflowMenuItem>
      </OverflowMenu>
    </>
  );
};

export default AgentDashboardOverflowMenu;
